import React, { useMemo, useState } from 'react';
import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import { FaSearch, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import RecuringJobLogViewModel from './RecuringJobLog.viewmodel';
import { formatYMDToDMY } from '../../../modules/Common';
import { useAdminCompanies } from '../../../hooks';

const PAGE_SIZE_OPTIONS = [50, 100, 200];

const JOB_TYPES = [
    { value: '', label: 'All' },
    { value: 'callback', label: 'Callback' },
    { value: 'esign', label: 'E-Sign' },
    { value: 'buffer', label: 'Buffer' },
    { value: 'notify_group', label: 'Notify Group' },
    { value: 'overdue_buffer', label: 'Overdue Buffer' },
    { value: 'daily_summary', label: 'Daily Summary' }
];

const STATUS_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'success', label: 'Success' },
    { value: 'error', label: 'Error' }
];

export default function RecuringJobLogView() {
    const { isLoading, logs, fetchLogs } = RecuringJobLogViewModel();
    const { masterCompanies } = useAdminCompanies();
    const masterCompanyMap = useMemo(
        () => masterCompanies
            ? new Map(masterCompanies.map(company => [company.sid, company]))
            : undefined,
        [masterCompanies]
    );

    const [searchSid, setSearchSid] = useState('');
    const [searchJobType, setSearchJobType] = useState('');
    const [searchStatus, setSearchStatus] = useState('');
    const [searchStartDate, setSearchStartDate] = useState('');
    const [searchEndDate, setSearchEndDate] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);

    const totalPages = useMemo(() => Math.max(1, Math.ceil(logs.length / pageSize)), [logs, pageSize]);
    const pagedLogs = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return logs.slice(start, start + pageSize);
    }, [logs, currentPage, pageSize]);
    const showFrom = logs.length > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const showTo = Math.min(currentPage * pageSize, logs.length);

    const handleSearch = () => {
        const startDate = searchStartDate ? searchStartDate.replace(/-/g, '') + '000000' : '';
        const endDate = searchEndDate ? searchEndDate.replace(/-/g, '') + '235959' : '';
        setCurrentPage(1);
        fetchLogs(searchSid, searchJobType, searchStatus, startDate, endDate);
    };

    const handleClear = () => {
        setSearchSid('');
        setSearchJobType('');
        setSearchStatus('');
        setSearchStartDate('');
        setSearchEndDate('');
    };

    return (
        <div>
            <div className="master-data-content">
                <div className="trapezoid-head" style={{ width: '250px' }}>
                    <span>Recurring Job Log</span>
                </div>

                <div className="mat-box">
                    <Row>
                        <Col>
                            <div className="trapezoid trapezoid-purple" style={{ width: '200px', display: 'inline-block' }}>
                                <span>
                                    <i className="fa fa-search" aria-hidden="true"></i>
                                    Search menu
                                </span>
                            </div>
                            <div className="mat-box shadow p-3 mb-5 bg-white rounded">
                                <Row>
                                    <Col md={2}>
                                        <Form.Group className="mb-3" controlId="searchSid">
                                            <Form.Label>SID :</Form.Label>
                                            <Form.Control type="text" size="sm" value={searchSid}
                                                onChange={e => setSearchSid(e.target.value)}
                                                onKeyPress={(e: any) => { if (e.key === 'Enter') handleSearch(); }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group className="mb-3" controlId="searchJobType">
                                            <Form.Label>Job Type :</Form.Label>
                                            <Form.Control as="select" size="sm" value={searchJobType}
                                                onChange={e => setSearchJobType(e.target.value)}>
                                                {JOB_TYPES.map(jt => (
                                                    <option key={jt.value} value={jt.value}>{jt.label}</option>
                                                ))}
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group className="mb-3" controlId="searchStatus">
                                            <Form.Label>Status :</Form.Label>
                                            <Form.Control as="select" size="sm" value={searchStatus}
                                                onChange={e => setSearchStatus(e.target.value)}>
                                                {STATUS_OPTIONS.map(s => (
                                                    <option key={s.value} value={s.value}>{s.label}</option>
                                                ))}
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group className="mb-3" controlId="searchStartDate">
                                            <Form.Label>Start Date :</Form.Label>
                                            <Form.Control type="date" size="sm" value={searchStartDate}
                                                onChange={e => setSearchStartDate(e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group className="mb-3" controlId="searchEndDate">
                                            <Form.Label>End Date :</Form.Label>
                                            <Form.Control type="date" size="sm" value={searchEndDate}
                                                onChange={e => setSearchEndDate(e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <div style={{ marginTop: '1.75rem', textAlign: 'right' }}>
                                            <Button variant="outline-info" size="sm" onClick={handleSearch}>
                                                <FaSearch /> Search
                                            </Button>
                                            <Button variant="outline-secondary" size="sm" className="mx-2" onClick={handleClear}>
                                                <FaTimes /> Clear
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <div className="container">
                                <div className="trapezoid trapezoid-purple" style={{ display: 'inline-block', width: 'fit-content' }}>
                                    <span>
                                        <i className="fa fa-list" aria-hidden="true"></i>
                                        Job Execution Log
                                    </span>
                                </div>
                                <div style={{ overflowX: 'auto' }} className="mt-2">
                                    <Table className="table-bordered table-hover table-sm">
                                        <thead>
                                            <tr className="table-active thead-light text-nowrap text-center">
                                                <th>Started</th>
                                                <th>Completed</th>
                                                <th>SID</th>
                                                <th>Job Type</th>
                                                <th>WF Type</th>
                                                <th>Status</th>
                                                <th>Duration</th>
                                                <th>Message</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {logs.length === 0 && (
                                                <tr>
                                                    <td colSpan={8} className="text-center text-muted py-3">
                                                        {isLoading ? 'Loading...' : 'กดปุ่ม Search เพื่อค้นหา'}
                                                    </td>
                                                </tr>
                                            )}
                                            {pagedLogs.map((log, idx) => {
                                                const isError = log.status === 'error';
                                                const rowStyle: React.CSSProperties = isError
                                                    ? { backgroundColor: '#f8d7da', color: '#721c24' }
                                                    : { backgroundColor: '#d4edda', color: '#155724' };
                                                return (
                                                    <tr key={idx} style={rowStyle}>
                                                        <td className="text-nowrap">{formatYMDToDMY(log.started_on ?? '')}</td>
                                                        <td className="text-nowrap">{formatYMDToDMY(log.completed_on ?? '')}</td>
                                                        <td>{masterCompanyMap?.get(log.sid ?? '')?.company_name ?? log.sid}</td>
                                                        <td><b>{log.job_type}</b></td>
                                                        <td>{log.wf_type}</td>
                                                        <td className="text-center"><b>{log.status}</b></td>
                                                        <td className="text-right text-nowrap">{log.duration_ms?.toLocaleString()} ms</td>
                                                        <td style={{ maxWidth: '400px', wordBreak: 'break-word' }}>{log.message}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                                {logs.length > 0 && (
                                    <div className="mt-3 flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-muted">Rows per page:</span>
                                            <Form.Control
                                                as="select"
                                                size="sm"
                                                style={{ width: '80px' }}
                                                value={pageSize}
                                                onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                                            >
                                                {PAGE_SIZE_OPTIONS.map(size => (
                                                    <option key={size} value={size}>{size}</option>
                                                ))}
                                            </Form.Control>
                                            <span className="text-sm text-muted ml-3">
                                                แสดง {showFrom}-{showTo} จาก {logs.length} รายการ
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                disabled={currentPage <= 1}
                                                onClick={() => setCurrentPage(p => p - 1)}
                                            >
                                                <FaChevronLeft />
                                            </Button>
                                            <span className="text-sm mx-2">
                                                หน้า {currentPage} / {totalPages}
                                            </span>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                disabled={currentPage >= totalPages}
                                                onClick={() => setCurrentPage(p => p + 1)}
                                            >
                                                <FaChevronRight />
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>
        </div>
    );
}
